import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

import { GraphQLClientService } from './graphql-client.service';
import { isErr } from '../models/api-result';
import { logError } from '../utils/error-sanitizer';
import {
  SUPPORT_SESSION_KEY,
  hasEnded,
  homeForNewSession,
  minutesRemaining,
  parseSupportSession,
  recordMatchesActiveCustomer,
  serialiseSupportSession,
  type SupportSessionRecord,
} from '../../operator/data/support-session.util';

const END_SUPPORT_SESSION_MUTATION = `
  mutation EndSupportSession {
    endSupportSession
  }
`;

/**
 * SupportSessionService — the Ricoh support session this tab is in, if any.
 *
 * RESPONSIBILITY
 *   Carries a support session across the switch's hard reload (see
 *   `support-session.util.ts`), exposes it to the shell's banner, and ends
 *   it. Starting one is `PlatformCustomerService.switchTo`, which calls
 *   `recordStarted` with what the API returned.
 *
 * SIGNALS
 *   session          — the record, or null outside a session
 *   minutesRemaining — whole minutes left (ticks once a minute)
 *   ended            — the session has run out; the API now refuses it
 *
 * USED BY
 *   App (banner), PlatformCustomerService (start), the operator console
 *   ("Back to customers").
 *
 * BACKEND CONTRACT
 *   `endSupportSession` — strips the session's roles and stamps it ended;
 *   the API also refuses an ended session on every request and on refresh,
 *   so the banner is information, not the enforcement.
 *
 * AI CONTEXT
 *   Read ONCE at construction against the active `customer-id`; a record for
 *   another customer is dropped. `recordStarted` is called just before a
 *   reload, so it writes storage only — the next boot reads it.
 */
@Injectable({ providedIn: 'root' })
export class SupportSessionService {
  private readonly graphql = inject(GraphQLClientService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _session = signal<SupportSessionRecord | null>(null);
  readonly session = this._session.asReadonly();

  private readonly _now = signal(Date.now());
  readonly minutesRemaining = computed(() => {
    const s = this._session();
    return s ? minutesRemaining(s.expiresAt, this._now()) : 0;
  });
  readonly ended = computed(() => {
    const s = this._session();
    return !!s && hasEnded(s.expiresAt, this._now());
  });

  constructor() {
    const record = parseSupportSession(this.read());
    const active = this.activeCustomerId();
    if (record && recordMatchesActiveCustomer(record, active)) {
      this._session.set(record);
      this.startClock();
    } else if (record && record.homeCustomerId !== active) {
      // Stale: another switch or sign-in since. Home is not stale — it is
      // simply "not in a session", and the next session reuses it.
      this.clear();
    }
  }

  /** The operator's own organisation, when known. */
  homeCustomerId(): string | null {
    return parseSupportSession(this.read())?.homeCustomerId ?? this.activeCustomerId();
  }

  /**
   * Park a session the API has just opened, for the boot the imminent reload
   * starts. Called by `PlatformCustomerService.switchTo`.
   */
  recordStarted(input: { customerId: string; customerName: string; expiresAt: string }): void {
    const home = homeForNewSession(parseSupportSession(this.read()), this.activeCustomerId());
    if (!home) return;
    this.write({ ...input, homeCustomerId: home });
  }

  /** The switch went home: nothing to show any more. */
  recordReturnedHome(): void {
    this.clear();
  }

  /**
   * End the session on the API (best-effort: it also ends on its own when it
   * runs out). Returns whether the API ended one.
   */
  async end(): Promise<boolean> {
    const result = await this.graphql.mutateResult<boolean>(END_SUPPORT_SESSION_MUTATION, {});
    if (isErr(result)) {
      logError('SupportSessionService.end', result.error.cause ?? result.error.message);
      return false;
    }
    return result.data === true;
  }

  private startClock(): void {
    // Once a minute is enough for "ends 17:40" and for "has ended".
    const id = setInterval(() => this._now.set(Date.now()), 60_000);
    this.destroyRef.onDestroy(() => clearInterval(id));
  }

  private activeCustomerId(): string | null {
    try {
      return localStorage.getItem('customer-id');
    } catch {
      return null;
    }
  }

  private read(): string | null {
    try {
      return localStorage.getItem(SUPPORT_SESSION_KEY);
    } catch {
      return null;
    }
  }

  private write(record: SupportSessionRecord): void {
    try {
      localStorage.setItem(SUPPORT_SESSION_KEY, serialiseSupportSession(record));
    } catch {
      /* storage unavailable — the banner is best-effort; the API enforces */
    }
  }

  private clear(): void {
    try {
      localStorage.removeItem(SUPPORT_SESSION_KEY);
    } catch {
      /* storage unavailable — nothing to clear */
    }
  }
}
